import React from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import ArrowUpIcon from '@wix/da-ds/pkg/Icons/16x16/ArrowUp';
import ArrowDownIcon from '@wix/da-ds/pkg/Icons/16x16/ArrowDown';
import EditIcon from '@wix/da-ds/pkg/Icons/16x16/Edit';
import TrashIcon from '@wix/da-ds/pkg/Icons/16x16/Trashcan';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import Checkbox from '@wix/da-ds/pkg/Checkbox';
import RadioButton from '@wix/da-ds/pkg/RadioButton';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import TextRenderer from '@wix/da-shared-react/pkg/TextRenderer';
import { formatDollars } from '@wix/da-shared-react/pkg/utils/formatting';
import QuestionViewer from '@wix/da-editor-tiptap/pkg/components/Viewer/presets/OriginalsQuestion';
import { Question } from '../../../../types';
import RequiredMarker from '../../../RequiredMarker';

import s from './QuestionCard.scss';

interface Props {
  question: Question;
  hasMoveUp: boolean;
  hasMoveDown: boolean;
  onMoveUpClick: () => void;
  onMoveDownClick: () => void;
  onEditClick: () => void;
  onRemoveClick: () => void;
  disableActions?: boolean;
}

const QuestionCard: React.FC<Props> = ({
  question,
  hasMoveUp,
  hasMoveDown,
  onMoveUpClick,
  onMoveDownClick,
  onEditClick,
  onRemoveClick,
  disableActions,
}) => {
  const { t } = useTranslation();
  return (
    <div className={s['root']}>
      <div className={s['actions']}>
        <div className={s['move-actions']}>
          {!disableActions && (
            <>
              <IconButton
                aria-labelledby="move-up"
                tooltipText={t(
                  'originalDeviationPopup.questionnaire.actions.moveUp'
                )}
                size="medium"
                variant="edit-purple"
                icon={ArrowUpIcon}
                onClick={onMoveUpClick}
                className={c(!hasMoveUp && s['move-action-disabled'])}
                disabled={!hasMoveUp}
              />
              <IconButton
                aria-labelledby="move-down"
                tooltipText={t(
                  'originalDeviationPopup.questionnaire.actions.moveDown'
                )}
                size="medium"
                variant="edit-purple"
                icon={ArrowDownIcon}
                onClick={onMoveDownClick}
                className={c(!hasMoveDown && s['move-action-disabled'])}
                disabled={!hasMoveDown}
              />
            </>
          )}
        </div>
        <div className={s['side-actions']}>
          <TextButton
            size="small"
            variant="edit-purple"
            icon={EditIcon}
            onClick={onEditClick}
            disabled={disableActions}
          >
            {t('common.edit')}
          </TextButton>
          <TextButton
            size="small"
            variant="edit-purple"
            icon={TrashIcon}
            onClick={onRemoveClick}
            disabled={disableActions}
          >
            {t('common.delete')}
          </TextButton>
        </div>
      </div>
      <hr className={s['divider']} />
      {question.isRequired && <RequiredMarker />}
      <div className={s['question-body']}>
        <TextRenderer
          textContent={question.question}
          tipTapViewer={QuestionViewer}
        />
      </div>
      {question.type === 'free_text' && (
        <div className={s['free-text-placeholder']}>
          <TextInput
            id="free-text-placeholder"
            label={t(
              'originalDeviationPopup.questionnaire.form.freeText.a11y.label'
            )}
            isLabelHidden
            placeholder={t(
              'originalDeviationPopup.questionnaire.form.freeText.placeholder'
            )}
            prefix={
              <EditIcon
                className={s['free-text-icon']}
                size={IconSize.SMALL}
                disableDefaultColors
              />
            }
            disabled
            noBottomSpacing
          />
        </div>
      )}

      <div className={s['answer-options']}>
        {question.answers?.map((choice, index) => (
          <div className={s['answer-option']} key={index}>
            <div className={s['answer-option-input']}>
              {question.type === 'multiple_choice' && (
                <Checkbox
                  size="small"
                  originalGreen
                  isChecked={false}
                  disabled
                />
              )}
              {question.type === 'single_choice' && (
                <RadioButton
                  name="answer-option"
                  value={choice.answer}
                  checked={false}
                  disabled
                />
              )}
            </div>
            <div className={s['answer-option-text']}>{choice.answer}</div>
            {choice.priceModifier && choice.priceModifier > 0 ? (
              <div className={s['answer-option-price']}>
                {formatDollars(choice.priceModifier)}
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
};
QuestionCard.displayName = 'QuestionCard';

export default QuestionCard;
